import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "../ui/sheet";
import { NAV } from "../../constants/testIds";
import { COMPANY } from "../../lib/site-data";
import { LOGO_URL } from "../../lib/brand";

const LINKS = [
  { to: "/", label: "Home", testid: NAV.linkHome, end: true },
  { to: "/services", label: "Services", testid: NAV.linkServices },
  { to: "/portfolio", label: "Portfolio", testid: NAV.linkPortfolio },
  { to: "/about", label: "About", testid: NAV.linkAbout },
  { to: "/contact", label: "Contact", testid: NAV.linkContact },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid={NAV.root}
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-[color:var(--surface-border)]"
          : "bg-white/70 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="container-tg flex items-center justify-between py-4">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-3 group">
          <img
            src={LOGO_URL}
            alt="Tecnografic logo"
            className="h-12 w-auto object-contain"
          />
          <span className="sr-only">{COMPANY.name}</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testid}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-semibold tracking-wide transition-colors ${
                  isActive
                    ? "text-[color:var(--brand-navy)]"
                    : "text-slate-600 hover:text-[color:var(--brand-navy)]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute left-4 right-4 -bottom-0.5 h-0.5 transition-all ${
                      isActive
                        ? "bg-[color:var(--brand-yellow)]"
                        : "bg-transparent"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={`tel:${COMPANY.phone}`}
            className="hidden xl:flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-[color:var(--brand-navy)]"
          >
            <Phone className="w-4 h-4" />
            {COMPANY.phone}
          </a>
          <Link to="/contact" data-testid={NAV.ctaQuote} className="btn-primary text-xs">
            Request a Quote
          </Link>
        </div>

        {/* Mobile */}
        <div className="lg:hidden flex items-center gap-2">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid={NAV.mobileToggle}
                aria-label="Toggle navigation"
                className="p-2 text-[color:var(--brand-navy)]"
              >
                {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              data-testid={NAV.mobileSheet}
              className="bg-white w-[88%] sm:w-[420px] border-l border-[color:var(--surface-border)] p-0"
            >
              <SheetTitle className="sr-only">Site navigation</SheetTitle>
              <div className="flex flex-col h-full">
                <div className="px-6 py-6 border-b border-[color:var(--surface-border)] flex items-center gap-3">
                  <img src={LOGO_URL} alt="Tecnografic" className="h-12 w-auto object-contain" />
                </div>
                <nav className="flex-1 px-6 py-6 flex flex-col gap-1">
                  {LINKS.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      end={l.end}
                      data-testid={`${l.testid}-mobile`}
                      className={({ isActive }) =>
                        `px-3 py-3 text-lg font-semibold border-l-2 transition-all ${
                          isActive
                            ? "border-[color:var(--brand-yellow)] text-[color:var(--brand-navy)] bg-[color:var(--brand-yellow)]/10"
                            : "border-transparent text-slate-700 hover:text-[color:var(--brand-navy)] hover:border-[color:var(--surface-border)]"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </nav>
                <div className="px-6 py-6 border-t border-[color:var(--surface-border)] flex flex-col gap-3">
                  <a
                    href={`tel:${COMPANY.phone}`}
                    className="flex items-center gap-2 text-sm font-semibold text-slate-700"
                  >
                    <Phone className="w-4 h-4" /> {COMPANY.phone}
                  </a>
                  <Link to="/contact" className="btn-primary text-xs w-full">
                    Request a Quote
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
